import React from 'react';
import { createBrowserRouter, Navigate, type RouteObject, RouterProvider } from 'react-router-dom';

import ReactRouterErrorMessageBlock from '@dashboard/components/pages/ReactRouterErrorMessageBlock';
import AdministratorPageLayout from '@dashboard/layouts/AdministratorPageLayout';
import ApplicationError from '@dashboard/layouts/ApplicationError';
import ApplicationPageLayout from '@dashboard/layouts/ApplicationPageLayout';
import PlatformContextPageLayout from '@dashboard/layouts/PlatformContextPageLayout';
import PlatformPageLayout from '@dashboard/layouts/PlatformPageLayout';
import RequireAuthenticationPageLayout from '@dashboard/layouts/RequireAuthenticationPageLayout';
import UnauthenticatedPageLayout from '@dashboard/layouts/UnauthenticatedPageLayout';
import UserManagementPageLayout from '@dashboard/layouts/UserManagementPageLayout';

import AdministratorPlatformShowPage from './pages/administrator/AdministratorPlatformShowPage';
import AdministratorPlatformsListPage from './pages/administrator/AdministratorPlatformsListPage';
import DomainNameVerificationShow from './pages/administrator/DomainNameVerificationShow';
import DomainNameVerificationsPage from './pages/administrator/DomainNameVerificationsPage';
import EmailMessagesListPage from './pages/administrator/EmailMessagesListPage';
import UsersList from './pages/administrator/UsersList';
import Auth0Callback from './pages/Auth0Callback';
import Auth0Logout from './pages/Auth0Logout';
import Auth0LogoutConfirm from './pages/Auth0LogoutConfirm';
import Auth0Signup from './pages/Auth0Signup';
import InvitePage from './pages/invite';
import NotFoundPage from './pages/NotFoundPage';
import WaypointEventsList from './pages/platforms/Activity';
import ContactShow from './pages/platforms/ContactShow';
import ContactsList from './pages/platforms/ContactsList';
import DomainNameVerificationPage from './pages/platforms/DomainNameVerificationPage';
import EmailMessageTemplateRaw from './pages/platforms/EmailMessageTemplateRaw';
import GroupShow from './pages/platforms/GroupShow';
import GroupsList from './pages/platforms/GroupsList';
import PlatformApiKeyNew from './pages/platforms/PlatformApiKeyNew';
import PlatformDeletePage from './pages/platforms/PlatformDeletePage';
import PlatformSettingsPage from './pages/platforms/PlatformSettingsPage';
import RequestLogList from './pages/platforms/RequestLogList';
import DisplayRequestLog from './pages/platforms/RequestLogList/DisplayRequestLog';
import EmptyRequestLog from './pages/platforms/RequestLogList/EmptyRequestLog';
import WaypointEventShow from './pages/platforms/WaypointEventShow';
import WaypointTemplateEdit from './pages/platforms/WaypointTemplateEdit';
import WaypointTemplatesList from './pages/platforms/WaypointTemplatesList';
import WorkflowEdit from './pages/platforms/WorkflowEdit';
import WorkflowEventShow from './pages/platforms/WorkflowEventShow';
import WorkflowRunsList from './pages/platforms/WorkflowRunsList';
import WorkflowsList from './pages/platforms/WorkflowsList';
import EditUserProfilePage from './pages/users/EditUserProfilePage';
import PlatformNewPage from './pages/users/PlatformNewPage';
import PlatformsListPage from './pages/users/PlatformsListPage';
import VerificationFailure from './pages/verification/VerificationFailure';
import VerificationSuccess from './pages/verification/VerificationSuccess';

const ROUTES: RouteObject[] = [
  {
    errorElement: <ApplicationError />,
    element: <ApplicationPageLayout />,
    children: [
      {
        element: <UnauthenticatedPageLayout />,
        children: [
          { path: '*', element: <NotFoundPage /> },
          { path: '/auth0/callback', element: <Auth0Callback /> },
          { path: '/signup', element: <Auth0Signup /> },
          { path: '/logout', element: <Auth0Logout /> },
          { path: '/logout/confirmation', element: <Auth0LogoutConfirm /> },
          { path: '/invites/o/:platformUserId/:inviteSecret', element: <InvitePage /> },
          { path: '/verification/success', element: <VerificationSuccess /> },
          { path: '/verification/failure', element: <VerificationFailure /> },
        ],
      },
      {
        element: <RequireAuthenticationPageLayout />,
        children: [
          {
            path: 'admin',
            element: <AdministratorPageLayout />,
            children: [
              { path: '', element: <AdministratorPlatformsListPage /> },
              { path: 'domains', element: <DomainNameVerificationsPage /> },
              { path: 'domains/:id', element: <DomainNameVerificationShow /> },
              { path: 'email-messages', element: <EmailMessagesListPage /> },
              { path: 'users', element: <UsersList /> },
              { path: 'o/:platformId', element: <AdministratorPlatformShowPage /> },
            ],
          },
          {
            element: <UserManagementPageLayout />,
            children: [
              { path: '', element: <PlatformsListPage /> },
              { path: 'organizations/new', element: <PlatformNewPage /> },
              { path: 'settings/profile', element: <EditUserProfilePage /> },
              { path: 'settings', element: <Navigate to="/settings/profile" replace /> },
            ],
          },
          {
            path: 'o/:platformId',
            element: <PlatformContextPageLayout />,
            children: [
              { path: 'workflows/:id', element: <WorkflowEdit /> },
              { path: 'templates/:id/raw', element: <EmailMessageTemplateRaw /> },
              { path: 'templates/:id', element: <WaypointTemplateEdit /> },
              {
                element: <PlatformPageLayout />,
                children: [
                  { path: '*', element: <NotFoundPage /> },
                  { path: '', element: <Navigate to="events" replace /> },
                  { path: 'events', element: <WaypointEventsList /> },
                  { path: 'delete', element: <PlatformDeletePage /> },
                  {
                    path: 'events/:id',
                    element: <WaypointEventShow />,
                    errorElement: <ReactRouterErrorMessageBlock />,
                  },
                  { path: 'domain-names/:id', element: <DomainNameVerificationPage /> },
                  {
                    path: 'request-logs',
                    element: <RequestLogList />,
                    children: [
                      { path: '', element: <EmptyRequestLog /> },
                      { path: ':requestLogId', element: <DisplayRequestLog /> },
                    ],
                  },

                  { path: 'workflow-runs', element: <WorkflowRunsList /> },
                  {
                    path: 'workflow-runs/:id',
                    element: <WorkflowEventShow />,
                    errorElement: <ReactRouterErrorMessageBlock />,
                  },
                  { path: 'templates', element: <WaypointTemplatesList /> },
                  { path: 'workflows', element: <WorkflowsList /> },
                  { path: 'contacts', element: <ContactsList /> },
                  { path: 'contacts/:id', element: <ContactShow /> },
                  { path: 'groups', element: <GroupsList /> },
                  { path: 'groups/:id', element: <GroupShow /> },

                  { path: 'settings', element: <PlatformSettingsPage /> },
                  { path: 'settings/api-keys/new', element: <PlatformApiKeyNew /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

const router = createBrowserRouter(ROUTES);

export default function Router() {
  return <RouterProvider router={router} />;
}
